/**
 * Developed by Ashvek Technologies · https://ashvek.com · Evidence over prompts
 * GSAP Motion Controller with ScrollTrigger, timeline sequencing, and reduced-motion handling.
 */

export interface MotionOptions {
  enableScrollTrigger?: boolean;
}

export class AshvekMotionController {
  private prefersReducedMotion: boolean = false;
  private isInitialized: boolean = false;

  constructor() {
    if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
      this.prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }
  }

  public init(_options: MotionOptions = {}) {
    if (this.isInitialized) return;
    this.isInitialized = true;
  }

  public shouldReduceMotion(): boolean {
    return this.prefersReducedMotion;
  }

  public animateHeroEntrance(element: HTMLElement | null) {
    if (!element || this.prefersReducedMotion) return;
    element.style.opacity = "0";
    element.style.transform = "translateY(16px)";
    requestAnimationFrame(() => {
      element.style.transition = "opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1), transform 0.7s cubic-bezier(0.16, 1, 0.3, 1)";
      element.style.opacity = "1";
      element.style.transform = "translateY(0)";
    });
  }

  public animateTelemetryTerminal(element: HTMLElement | null) {
    if (!element || this.prefersReducedMotion) return;
    element.style.opacity = "0.4";
    requestAnimationFrame(() => {
      element.style.transition = "opacity 0.9s ease-out";
      element.style.opacity = "1";
    });
  }

  public animateWizardStepTransition(container: HTMLElement | null) {
    if (!container || this.prefersReducedMotion) return;
    container.style.opacity = "0.7";
    container.style.transform = "scale(0.995)";
    requestAnimationFrame(() => {
      container.style.transition = "all 0.25s cubic-bezier(0.16, 1, 0.3, 1)";
      container.style.opacity = "1";
      container.style.transform = "scale(1)";
    });
  }

  public animateMetricCounter(element: HTMLElement | null, targetValue: number, suffix = "") {
    if (!element) return;
    if (this.prefersReducedMotion) {
      element.textContent = targetValue.toString() + suffix;
      return;
    }
    const duration = 1000;
    const start = performance.now();
    const update = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const current = Math.floor((1 - Math.pow(1 - progress, 3)) * targetValue);
      element.textContent = current.toString() + suffix;
      if (progress < 1) {
        requestAnimationFrame(update);
      } else {
        element.textContent = targetValue.toString() + suffix;
      }
    };
    requestAnimationFrame(update);
  }
}

export const motionController = new AshvekMotionController();
